import type { FunctionComponent } from 'react';
import {
  CardCvvElement,
  CardMonthElement,
  CardNumberElement,
  CardYearElement,
} from '@recurly/react-recurly';
import { ICreditCardForm, IFormField } from '../../../../../interfaces';
import useListFieldState from '../../hooks/useListFieldState';
import FormField from '../FormField/FormField';
import * as styles from './CreditCardForm.module.css';

const recurlyElementStyles = {
  placeholder: {
    color: 'rgba(0, 0, 0, 0.4)',
    fontFamily: 'Nunito Sans, sans-serif',
    fontSize: '14px',
    fontWeight: '400',
  },
  style: {
    borderRadius: '3px !important',
  },
};

const CreditCardForm: FunctionComponent<ICreditCardForm> = ({
  location = 'GTB',
  name,
  lastName,
  number,
  month,
  year,
  cvv,
  postalCode,
  billingCountry,
  eventData,
}) => {
  const expirationDate = useListFieldState([month, year]);

  const zipCodeInputConfig: Pick<IFormField, 'type' | 'pattern' | 'inputMode'> = {
    type: billingCountry === 'US' ? 'number' : 'text',
    pattern: billingCountry === 'US' ? '[0-9]*' : undefined,
    inputMode: billingCountry === 'US' ? 'numeric' : undefined,
  };

  const getFocusHandler = (fieldName: string) =>
    eventData
      ? () =>
          name.focusHandler({
            fieldName,
            section: eventData.section,
            formType: eventData.formType,
          })
      : undefined;

  return (
    <>
      <div className={styles.pffRow}>
        <FormField
          location={location}
          label="First Name"
          placeholder="First Name"
          id="first_name"
          dataRecurly="first_name"
          fieldStatus={name}
          type="text"
          onFocus={getFocusHandler('firstName')}
        />
        <FormField
          location={location}
          label="Last Name"
          placeholder="Last Name"
          id="last_name"
          dataRecurly="last_name"
          fieldStatus={lastName}
          type="text"
          onFocus={getFocusHandler('lastName')}
        />
      </div>
      <div className={styles.pffRow}>
        <FormField
          location={location}
          label="Credit Card"
          id="number"
          fieldStatus={number}
          type="text"
        >
          <CardNumberElement
            style={{
              ...recurlyElementStyles,
              placeholder: {
                ...recurlyElementStyles.placeholder,
                content: 'Card Number',
              },
            }}
            onChange={number.changeHandler}
            onBlur={number.blurHandler}
            onFocus={getFocusHandler('ccNumber')}
          />
        </FormField>
      </div>
      <div className={styles.pffRow}>
        <div className={styles.pffCol}>
          <FormField
            location={location}
            label="Expiration Date"
            id="card_date"
            fieldStatus={expirationDate}
            type="text"
          >
            <div className={styles.pffInline}>
              <CardMonthElement
                style={{
                  ...recurlyElementStyles,
                  placeholder: {
                    ...recurlyElementStyles.placeholder,
                    content: 'MM',
                  },
                }}
                onChange={month.changeHandler}
                onBlur={month.blurHandler}
                onFocus={getFocusHandler('expirationMonth')}
              />
              <CardYearElement
                style={{
                  ...recurlyElementStyles,
                  placeholder: {
                    ...recurlyElementStyles.placeholder,
                    content: 'YY',
                  },
                }}
                onChange={year.changeHandler}
                onBlur={year.blurHandler}
                onFocus={getFocusHandler('expirationYear')}
              />
            </div>
          </FormField>
        </div>
        <div className={styles.pffCol}>
          <FormField location={location} label="CVV" id="card_cvv" fieldStatus={cvv} type="text">
            <CardCvvElement
              style={{
                ...recurlyElementStyles,
                placeholder: {
                  ...recurlyElementStyles.placeholder,
                  content: 'CVV',
                },
              }}
              onChange={cvv.changeHandler}
              onBlur={cvv.blurHandler}
              onFocus={getFocusHandler('cvv')}
            />
          </FormField>
          {location !== 'GTB' && (
            <FormField
              location={location}
              label="Zip/ Postal Code"
              placeholder="Billing Zip / Postal Code"
              id="postal_code"
              dataRecurly="postal_code"
              fieldStatus={postalCode}
              {...zipCodeInputConfig}
            />
          )}
        </div>
        {location === 'GTB' && (
          <FormField
            location={location}
            label="Zip/ Postal Code"
            placeholder="Billing Zip / Postal Code"
            id="postal_code"
            dataRecurly="postal_code"
            fieldStatus={postalCode}
            onFocus={getFocusHandler('zipCode')}
            {...zipCodeInputConfig}
          />
        )}
      </div>
    </>
  );
};

export default CreditCardForm;
